import _ from 'es-toolkit/compat';
import { read, utils, WorkSheet, write } from 'xlsx';

import { FileType } from '@clearverify/constants';
import type { Nullable, SimpleAnyObject } from '@clearverify/types';

import { FileInfoFragment } from 'src/api';

export const readExcelFile = async (file: Nullable<File>) => {
  if (!file) return null;
  const buffer = await file.arrayBuffer();
  return read(buffer);
};

export const trimExcelFileHeadings = <TItem extends Nullable<string>>(
  headings: TItem[] | undefined,
) => {
  return headings?.map(item => (item ? item.trim() : item));
};

export const downloadXlsxFilteredBySchema = async <TSchema extends SimpleAnyObject>(
  file: Nullable<FileInfoFragment>,
  schema: Nullable<TSchema>,
): Promise<void> => {
  if (!file?.downloadUrl || !schema) return undefined;

  const sourceData = await (await fetch(file.downloadUrl)).arrayBuffer();
  const sourceWorkbook = read(sourceData);

  let sourceWorkbookData: string[][] = [];
  let sourceSheet: WorkSheet | undefined;
  let sourceSheetName = '';

  for (sourceSheetName of sourceWorkbook.SheetNames) {
    sourceSheet = sourceWorkbook.Sheets[sourceSheetName];

    if (!sourceSheet) throw new Error('Excel Worksheet not found');

    sourceWorkbookData = utils.sheet_to_json(sourceSheet, {
      header: 1,
      blankrows: false,
    });
    const headings = sourceWorkbookData[0] as string[] | undefined;

    if (headings?.includes(schema.ssn)) {
      break;
    }
  }

  if (!sourceSheet) throw new Error('Excel Worksheet not found');

  const workbook = utils.book_new();
  const workbookFilename = file.filename ? file.filename.replace(/\.(xlsx|xls)$/i, '') : 'file';

  workbook.Props = {
    Title: workbookFilename,
  };

  const schemaValues = Object.values(schema);

  const schemaFilteredColumnsIndexes =
    sourceWorkbookData[0]?.reduce<number[]>((indexes, columnName, index) => {
      if (_.includes(schemaValues, _.trim(columnName))) {
        return [...indexes, index];
      }
      return indexes;
    }, []) || [];

  const schemaFilteredRows = sourceWorkbookData.map(sourceRow => {
    return schemaFilteredColumnsIndexes.map(schemaColumnIndex => sourceRow[schemaColumnIndex]);
  });

  const schemaFilteredWorkSheet = utils.aoa_to_sheet(schemaFilteredRows);

  utils.book_append_sheet(workbook, schemaFilteredWorkSheet, sourceSheetName);

  const workbookFile = write(workbook, { bookType: 'xlsx', type: 'buffer' });

  const xlsxBlob = new Blob([workbookFile], {
    type: `data:${FileType.xlsx};`,
  });

  const downloadLink = document.createElement('a');
  downloadLink.href = URL.createObjectURL(xlsxBlob);
  downloadLink.download = `${workbookFilename}.xlsx`;
  downloadLink.click();

  return undefined;
};
